import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Heart } from "lucide-react";

const footerLinks = {
  services: [
    { name: "Supported Accommodation", path: "/services#accommodation" },
    { name: "Mentoring & Programmes", path: "/services#mentoring" },
    { name: "Wraparound Support", path: "/services#wraparound" },
  ],
  company: [
    { name: "About Us", path: "/about" },
    { name: "Safeguarding", path: "/safeguarding" },
    { name: "Impact & Outcomes", path: "/impact" },
    { name: "Work With Us", path: "/work-with-us" },
  ],
  resources: [
    { name: "Make a Referral", path: "/referrals" },
    { name: "Contact Us", path: "/contact" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Policies", path: "/policies" },
  ],
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-6" data-testid="footer-logo">
              <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">EC</span>
              </div>
              <span className="font-bold text-xl text-white">Epic Care</span>
            </Link>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Providing safe, supportive pathways for young people to develop
              stability, independence and positive futures.
            </p>
            <div className="space-y-3">
              <a
                href="mailto:info@epiccareltd.co.uk"
                className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5" />
                info@epiccareltd.co.uk
              </a>
              <a
                href="tel:+44"
                className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
                Contact for phone number
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>United Kingdom</span>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold text-white mb-6">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-teal-400 transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Safeguarding Statement */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="bg-slate-800/50 rounded-xl p-6">
            <h5 className="font-semibold text-white mb-2 flex items-center gap-2">
              <Heart className="w-5 h-5 text-teal-400" />
              Safeguarding Statement
            </h5>
            <p className="text-slate-400 text-sm leading-relaxed">
              Epic Care is committed to safeguarding and promoting the welfare of
              all young people in our care. We have robust safeguarding policies
              and procedures in place, and all staff undergo enhanced DBS checks
              and safeguarding training.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Epic Care Ltd. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              Company Registration Number: [Placeholder]
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
